import React, { MouseEvent, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Row } from "antd/es/grid";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";
import { getPreviewEntityUniqueElementId } from "@mapmycustomers/shared/util/browser";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { Avatar, LoadingSpinner } from "@mapmycustomers/ui";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import ButtonLink from "@app/component/ButtonLink";
import Chip from "@app/component/Chip";
import CollapseCard from "@app/component/CollapseCard";
import { ACTIVITIES_PORTION_SIZE } from "@app/component/frequency/util/const";
import { showEntityView } from "@app/store/entityView/actions";
import {
  getFrequencyModalConfig,
  getPreviousActivities,
  getPreviousActivitiesTotal,
  isPreviousActivitiesLoading,
} from "@app/store/frequency";
import { clearPreviousActivities, fetchPreviousActivities } from "@app/store/frequency/actions";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import layout from "@app/styles/layout";
import getOverdueStatus from "@app/util/activity/getOverdueStatus";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import styles from "./PreviousActivities.module.scss";

const PreviousActivities = ({
  clearPreviousActivities,
  entity,
  loading,
  onFetchPreviousActivities,
  previousActivities,
  previousActivitiesTotal,
  showEntityView,
  subscribe,
  unsubscribe,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const [offset, setOffset] = useState<number>(0);
  const analytics = useAnalytics();

  const increaseOffset = useDebouncedCallback(
    [
      () => {
        setOffset((value) => value + ACTIVITIES_PORTION_SIZE);
      },
      100,
    ],
    [setOffset]
  );

  const resetOffset = useCallback(() => {
    clearPreviousActivities();
    if (offset === 0) {
      onFetchPreviousActivities({
        offset: 0,
      });
    } else {
      setOffset(0);
    }
  }, [clearPreviousActivities, offset, onFetchPreviousActivities, setOffset]);

  useEffect(() => {
    onFetchPreviousActivities({
      offset,
    });
  }, [offset, onFetchPreviousActivities]);

  useEffect(() => {
    const listener = {
      onAdd: resetOffset,
      onUpdate: resetOffset,
    };

    subscribe({ entityTypes: [entity.entity, EntityType.ACTIVITY], listener });

    return () => {
      unsubscribe({ entityTypes: [entity.entity, EntityType.ACTIVITY], listener });
    };
  }, [entity, resetOffset, subscribe, unsubscribe]);

  const handleOpenPanel = useCallback(() => {
    analytics.clicked(["Previous Activities"]);
  }, [analytics]);

  const handleActivityClick = useCallback(
    (event: MouseEvent, activityId: Activity["id"]) => {
      showEntityView({
        elementId: event?.target
          ? getPreviewEntityUniqueElementId(event.target as HTMLElement)
          : undefined,
        entityId: activityId,
        entityType: EntityType.ACTIVITY,
      });
    },
    [showEntityView]
  );

  return (
    <div className={styles.container}>
      <CollapseCard
        header={intl.formatMessage({
          id: "frequency.frequencyModal.activities.previous.title",
          defaultMessage: "previous activities",
          description: "frequency component - frequency modal - previous activities - title",
        })}
        onOpen={handleOpenPanel}
      >
        {previousActivitiesTotal > previousActivities.length && (
          <ButtonLink onClick={increaseOffset}>
            {intl.formatMessage({
              id: "frequency.frequencyModal.activities.previous.showEarlier",
              defaultMessage: "Show earlier activities",
              description:
                "frequency component - frequency modal - previous activities - Show earlier activities",
            })}
          </ButtonLink>
        )}
        <div className={styles.activityList}>
          {previousActivities.map((activity) => {
            const {
              id,
              assignee,
              completed,
              crmActivityType,
              emailLog,
              name,
              note,
              reliability,
              startAt,
            } = activity;
            return (
              <div
                className={styles.activity}
                key={id}
                onClick={(e) => handleActivityClick(e, activity.id)}
              >
                <Row justify="space-between">
                  <Col>
                    <Row align="middle" gutter={layout.spacerXS}>
                      <Col>
                        <Tooltip
                          title={intl.formatMessage(
                            {
                              id: "frequency.frequencyModal.activities.previous.tooltip",
                              defaultMessage: "{activityName} with {companyName}",
                              description:
                                "frequency component - frequency modal - previous activities - tooltip",
                            },
                            { activityName: name, companyName: entity.name }
                          )}
                        >
                          <Chip
                            className={styles.tag}
                            icon={
                              <ActivityTypeIcon
                                activityType={crmActivityType}
                                isAutoEmail={!!emailLog}
                                size="sm"
                                tooltipProps={false}
                              />
                            }
                          >
                            {crmActivityType.name}
                          </Chip>
                        </Tooltip>
                      </Col>
                      <Col>
                        <Tag className={styles.tag}>
                          {formatDate(parseApiDate(startAt ?? ""), "PPPppp")}
                        </Tag>
                      </Col>
                    </Row>
                  </Col>
                  <Col>
                    <Row align="middle" gutter={layout.spacerS}>
                      <Col>
                        {getOverdueStatus(activity) ? (
                          <Chip
                            icon={
                              <FontAwesomeIcon
                                className={styles.overdueIcon}
                                icon={faExclamationTriangle}
                              />
                            }
                            iconPosition="end"
                            type="light"
                          >
                            {intl.formatMessage({
                              id: "frequency.frequencyModal.activities.previous.activityOverdue",
                              defaultMessage: "Overdue",
                              description:
                                "frequency component - frequency modal - previous activities - Overdue",
                            })}
                          </Chip>
                        ) : completed ? (
                          <Chip
                            icon={
                              <FontAwesomeIcon className={styles.iconDone} icon={faCheckCircle} />
                            }
                            iconPosition="end"
                            type="light"
                          >
                            {intl.formatMessage({
                              id: "frequency.frequencyModal.activities.previous.activityDone",
                              defaultMessage: "Done",
                              description:
                                "frequency component - frequency modal - previous activities - done",
                            })}
                          </Chip>
                        ) : reliability ? (
                          <Chip
                            icon={
                              <FontAwesomeIcon
                                className={styles.iconVerified}
                                icon={faCheckCircle}
                              />
                            }
                            iconPosition="end"
                            type="light"
                          >
                            {intl.formatMessage({
                              id: "frequency.frequencyModal.activities.previous.activityVerified",
                              defaultMessage: "Verified",
                              description:
                                "frequency component - frequency modal - previous activities - Verified",
                            })}
                          </Chip>
                        ) : (
                          <div />
                        )}
                      </Col>
                      <Col>{assignee && <Avatar size="xs" tooltip={false} user={assignee} />}</Col>
                    </Row>
                  </Col>
                </Row>
                <div className={cn(styles.note, { [styles.noNote]: !note })}>
                  {note ??
                    intl.formatMessage({
                      id: "frequency.frequencyModal.activities.previous.noNote",
                      defaultMessage: "No note logged",
                      description:
                        "frequency component - frequency modal - previous activities - done",
                    })}
                </div>
              </div>
            );
          })}
        </div>
        {loading && <LoadingSpinner />}
        {!loading && !previousActivities.length && (
          <div className={styles.noActivities}>
            {intl.formatMessage({
              id: "frequency.frequencyModal.activities.previous.noActivities",
              defaultMessage: "no previous activities",
              description:
                "frequency component - frequency modal - previous activities - no previous activities",
            })}
          </div>
        )}
      </CollapseCard>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  entity: getFrequencyModalConfig(state).entity,
  loading: isPreviousActivitiesLoading(state),
  previousActivities: getPreviousActivities(state),
  previousActivitiesTotal: getPreviousActivitiesTotal(state),
});

const mapDispatchToProps = {
  clearPreviousActivities,
  onFetchPreviousActivities: fetchPreviousActivities.request,
  showEntityView,
  subscribe,
  unsubscribe,
};

export default connect(mapStateToProps, mapDispatchToProps)(PreviousActivities);
